import React, { useEffect } from 'react';
import { arrayOf, bool, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink, IconSpinner } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { fetchAllStates, getStatesData } from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';

import css from './SeoCategoriesPage.module.css';

const PAGE_TITLE = 'Trailer Rentals by State — All 50 States | BigLoads';
const PAGE_DESCRIPTION =
  'Browse trailer rentals in all 50 US states on BigLoads. Find utility trailers, flatbeds, enclosed cargo trailers and more — rent directly from local owners, not dealers. Choose your state to see available cities near you.';

const SeoCategoriesPageComponent = ({ scrollingDisabled, intl }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAllStates());
  }, []);

  const { statesData, searchInProgress } = useSelector(getStatesData);

  const siteUrl = process.env.REACT_APP_MARKETPLACE_ROOT_URL || 'https://bigloads.com';

  // ItemList JSON-LD — one list item per state
  const itemListSchema = {
    '@type': 'ItemList',
    name: 'Trailer Rentals by State',
    description: PAGE_DESCRIPTION,
    numberOfItems: isArrayLength(statesData) ? statesData.length : 50,
    itemListElement: isArrayLength(statesData)
      ? statesData.map((s, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          name: `Trailer Rentals in ${s?.title || s?.slug}`,
          url: `${siteUrl}/states/${s?.slug}`,
        }))
      : [],
  };

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Trailer Rentals by State',
        item: `${siteUrl}/states`,
      },
    ],
  };

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={PAGE_DESCRIPTION}
      title={PAGE_TITLE}
      schema={[breadcrumbSchema, itemListSchema]}
    >
      <LayoutSingleColumn
        className={css.root}
        topbar={<TopbarContainer isWhiteBgNav={false} isHeaderSticky={true} />}
        footer={<FooterContainer />}
      >
        <div className={css.pageRoot}>
          <div className={css.pageHeader}>
            <span>BigLoads — Peer-to-Peer Trailer Rental</span>
            <h1>Trailer Rentals by State</h1>
            <p>
              Find trailer rentals wherever you are in America. Select your state below to browse
              available cities, owner listings, and local rental options near you.
            </p>
          </div>

          <div className={css.otherLinksList}>
            {searchInProgress ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <IconSpinner />
              </div>
            ) : null}
            <ul>
              {isArrayLength(statesData) ? (
                statesData.map(s => (
                  <li key={s?.slug}>
                    <NamedLink name="StateListsPage" params={{ state: s?.slug }}>
                      {s?.title}
                    </NamedLink>
                  </li>
                ))
              ) : (
                <></>
              )}
            </ul>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoCategoriesPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const EnhancedSeoCategoriesPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();

  return (
    <SeoCategoriesPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const SeoCategoriesPage = compose(connect(mapStateToProps))(EnhancedSeoCategoriesPage);

export default SeoCategoriesPage;
